import React from 'react';
import styled from 'styled-components';

import App from '../../../models/app';
import { ACTIONS, ButtonComponentType, ButtonTextTranslations, MODALS, ModalType, ButtonType } from './types';
import InstallFlow from '../../../models/install-flow';
import {
  installApp,
  setSelectedClock,
  uninstallApp,
  installTile,
  uninstallTile
} from 'actions/gallery';
import { connectDeep } from 'redux-utils';
import { buttonComponents, modals } from './config';
import { AppType } from '../../../constants';
import { cancelAppInstall } from 'actions/registry';
import { translations } from '../../../i18n';
import ButtonBar from '../Button/ButtonBar';

const ButtonContainer = styled.div<{ $isContents?: boolean }>`
  ${({ $isContents }) => $isContents ? `
    display: contents;
  ` : `
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 16px;
    
    > * {
      flex: 1;
    }
  `}
`;

const mapStateToProps = ({
  gallery: {
    currentlySyncing,
    triggerWifiConnect: { loading }
  },
  gallery,
  registry,
  registry: {
    features: { flags }
  },
  ...state
}: any) => {

  const {
    installedTiles,
    installedApps
  } = registry

  let tiles = [];
  if (installedTiles && Object.keys(installedTiles).length > 0) {
    tiles = Object.keys(installedTiles).map((tileId: any) => App.selectFromState(tileId, { gallery, registry, ...state }))
  }

  return {
    tiles,
    installedApps,
    currentlySyncing,
    loading,
    features: { flags },
    requiresSync: InstallFlow.selectFromState({
      ...state,
      registry,
      gallery
    }).requiresSync,
  }
};

const mapDispatchToProps = (dispatch: any, { app }: any) => ({
  onInstall: () => {
    dispatch(installApp(app.id));
    if (app.isClock) {
      dispatch(setSelectedClock(app.id));
    }
  },
  onUninstall: () => dispatch(uninstallApp(app.id)),
  cancelAppInstall: () => {
    dispatch(cancelAppInstall(app.id));
  },
  onInstallTile: (parentInstalled: boolean) => dispatch(installTile(app.id, parentInstalled)),
  onUninstallTile: () => dispatch(uninstallTile(app.id))
});

type ReduxProps = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;

interface StateManagementButtonProps extends ReduxProps {
  app: App;
  parentApp?: App;
  showRemove?: boolean;
  texts: ButtonTextTranslations;
  action?: ACTIONS;
  CustomButton?: React.ComponentType<any>;
  collectionId?: string | null;
  onSuccess?: () => void;
  deviceName?: string;
  isContents?: boolean;
}

interface StateManagementButtonState {
  showModals: boolean;
  currentModal?: MODALS;
  button: ButtonComponentType;
}

class StateManagementButton extends React.Component<StateManagementButtonProps, StateManagementButtonState> {

  constructor(props: StateManagementButtonProps) {
    super(props);
    const { currentButton, currentModal } = StateManagementButton.getButtonAndModal(props);

    this.state = {
      button: currentButton as ButtonComponentType,
      showModals: false,
      currentModal,
    };
  }

  static getDerivedStateFromProps(props: StateManagementButtonProps, state: StateManagementButtonState) {
    const { button } = state;

    const updatedButton = StateManagementButton.getButtonAndModal(props);

    if (button.name === updatedButton.currentButton.name &&
      button.modals?.length == updatedButton.currentButton.modals?.length) {
      return null;
    }

    return {
      button: updatedButton.currentButton,
      currentModal: updatedButton.currentModal,
    };
  }

  renderModals = () => {
    const { app, deviceName, parentApp } = this.props;

    const currentModal = modals
      .find(({ name }) => name === this.state.currentModal);

    if (!currentModal) {
      // burn it
      return;
    }

    const { ModalComponent } = currentModal;

    return (
      <ModalComponent
        app={app}
        onCancel={this.onCancel}
        onSuccess={this.onSuccess}
        deviceName={deviceName}
        parentApp={parentApp}
      />
    );
  };

  render() {
    const { app, texts, CustomButton } = this.props;
    const { button, showModals } = this.state;

    const {
      cancel
    } = translations.page.App.buttons;

    let shouldDisplayCancelOption;
    const fitbitDeveloper = 'Fitbit';

    // clocks in the favorites list should not have the cancel option
    if (!this.props.app.isRemovable || (this.props.app.isClock && this.props.app.isOnDevice && this.props.app.isInstalling) ||
      (this.props.app.type.toLocaleLowerCase() === AppType.APP && this.props.app.isOnDevice && this.props.app.isInstalling) ||
      (this.props.app.type.toLocaleLowerCase() === AppType.APP && this.props.app.developer.name === fitbitDeveloper) ||
      (this.props.app.isPendingUpdate || this.props.app.isOnDevice || this.props.app.hasUpdate || this.props.app.hasUpdateAvailable
        || this.props.app.isStagedUpdate) ||
      (this.props.app.type.toLocaleLowerCase() === AppType.TILE) || (this.props.app.type.toLocaleLowerCase() === AppType.SERVICE)) {
      shouldDisplayCancelOption = false;
    } else {
      shouldDisplayCancelOption = app.isRemovable
        && this.props.app.isPendingInstall
        && !this.props.app.isOnDevice
        && (this.state.button.name === ButtonType.LOADING)
        && !app.isPendingRemoval;
    }

    // if the button does not have any modals it means we go straight for success
    const handler = button.modals?.length ? this.toggleModals : this.onSuccess;
    const additionalProps = typeof button.props === 'function' ? button.props(app) : button.props;

    let Display: any;
    if (button.ButtonComponent) {
      Display = button.ButtonComponent(app, { texts });
    } else if (CustomButton) {
      Display = <CustomButton
        onClick={handler}
        {...additionalProps}
      >{texts[button.textKey as string]}</CustomButton>
    } else {
      Display = <ButtonBar
        onClick={handler}
        variant='filled'
        fullWidth
        {...additionalProps}
      >{texts[button.textKey as string]}</ButtonBar>
    }

    return (
      <ButtonContainer $isContents={this.props.isContents}>
        {Display}
        {showModals && this.renderModals()}
        {shouldDisplayCancelOption && !CustomButton && (
          <ButtonBar variant='outline' fullWidth onClick={() => { this.triggerCancelInstall() }}>
            {cancel()}
          </ButtonBar>
        )}
      </ButtonContainer>
    );
  }

  toggleModals = () => {
    this.setState({ showModals: !this.state.showModals });
  }

  onCancel = () => {
    const { currentModal } = this.state;
    const { app } = this.props;

    this.toggleModals();
  }

  onSuccess = () => {
    const { button, currentModal } = this.state;
    const { app, collectionId } = this.props;

    if (button.modals && currentModal) {
      const currentModalIndex = button.modals.indexOf(currentModal);

      // it means there are no more modals, we can execute the action or exit
      if (currentModalIndex + 1 === button.modals.length) {
        this.triggerAction(button.action as ACTIONS, app);
        this.setState({ showModals: false });
        return;
      }

      this.setState({ currentModal: button.modals[currentModalIndex + 1] });
      return;
    }

    // trigger action in the end for buttons without modals(install/update)
    this.triggerAction(button.action as ACTIONS, app);
  }

  /**
   * Get the current modal and button for any given props
   * @param props
   * @private
   */
  private static getButtonAndModal(props: StateManagementButtonProps) {
    const { app, loading, showRemove, onUninstall, action, features } = props;
    const foundButton = buttonComponents
      .find((buttonComponent) => buttonComponent.shouldDisplay(app, {
        loading,
        showRemove,
        onUninstall,
        action
      })) as ButtonComponentType;
    // clone the button uses to keep the config untainted
    const currentButton = Object.assign({}, foundButton);

    let currentModal: MODALS | undefined;
    if (currentButton.modals?.length) {
      // get all the modals assigned to this button
      const availableModals = modals.filter(({ name }) => currentButton.modals?.includes(name));
      // get all the modals which should be displayed for this button
      const visibleModals = StateManagementButton.getNextModals(props, availableModals);
      currentModal = visibleModals.length ? visibleModals[0].name : undefined;

      // refresh the modals with just those that need to be visible
      currentButton.modals = visibleModals.map((modal) => modal.name);
    }

    return { currentButton, currentModal };
  }

  /**
   * Get the next available modals from a list of existing modals
   * @param props
   * @param modalsToFind
   * @private
   */
  private static getNextModals(props: StateManagementButtonProps, modalsToFind: ModalType[]): ModalType[] {
    const {
      app,
      currentlySyncing,
      requiresSync,
      parentApp,
      tiles,
      installedApps,
      features
    } = props;

    let parentAppInstalled = false
    if (app.type.toLocaleLowerCase() === AppType.TILE && parentApp) {
      parentAppInstalled = Object.keys(installedApps).includes(parentApp?.id)
    }

    return modalsToFind
      .filter((modal) => modal.shouldDisplay(app, {
        currentlySyncing,
        requiresSync,
        parentApp,
        parentAppInstalled,
        tiles,
        features
      }));
  }

  private triggerCancelInstall() {
    const app = this.props.app;
    if (app.isRemovable && app.isPendingInstall && !app.isOnDevice && (app.isClock || app.isApp)) {
      this.props.cancelAppInstall();
    }
  }

  private triggerAction(action: ACTIONS, app: App) {
    const { onSuccess, installedApps, parentApp } = this.props;

    if (onSuccess) {
      onSuccess();
    }
    // install/update are the same from our point of view
    if (action === ACTIONS.INSTALL || action === ACTIONS.UPDATE) {
      if (app.type.toLocaleLowerCase() === AppType.TILE) {
        let parentAppInstalled = false
        if (parentApp) {
          parentAppInstalled = Object.keys(installedApps).includes(parentApp?.id)
        }

        this.props.onInstallTile(parentAppInstalled);
        return
      }

      this.props.onInstall();
      return;
    }

    if (action === ACTIONS.UNINSTALL) {
      if (app.type.toLocaleLowerCase() === AppType.TILE) {
        this.props.onUninstallTile();
        return
      }

      this.props.onUninstall();
    }
  }

}

export default connectDeep(
  mapStateToProps,
  mapDispatchToProps
)(StateManagementButton);
